import React from 'react';

import {
  AlineTransforms,
  FooterType,
  HeaderType,
  isEvenNum,
  pageSeparationPx,
} from '@aline/common';
import { Typography } from '@mui/material';
import { Editor, Range } from 'slate';
import { SelectButton } from 'src/components';
import { FooterIcon } from 'src/components/icons/FooterIcon';
import { HeaderIcon } from 'src/components/icons/HeaderIcon';
import { LinkIcon } from 'src/components/icons/LinkIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { insertLinkPopoverVar } from 'src/editor/popovers';
import { Queries } from 'src/editor/queries';
import { InsertTableButton, PageBreakButton } from 'src/editor/toolbars/tabs/insert-tab';
import { ClientTransforms } from 'src/editor/transforms';
import { useEditor, useEditorLoading, useSlateSelector } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { CrossReferenceButton } from './CrossReferenceButton';
import { InsertImageButton } from './ImageButton';
import { PageNumbersButton } from './PageNumbersButton';
import { TableOfContentsButton } from './TableOfContentsButton';
import { ButtonsBarWithOverflowMenu } from '../shared/ButtonsBarWithOverflowMenu';

const useStyles = makeStyles()(theme => ({
  buttonText: {
    paddingLeft: theme.spacing(0.5),
  },
}));

export function attachInsertLinkPopper(editor: Editor): void {
  if (!editor.selection || Queries.rangeIncludesCheckboxElement(editor, editor.selection)) return;

  /**
   * First, we resolve the link selection since the selection may encompass inlines.
   * This acts to change the selection to the correct place if there are inlines present and return a Range corresponding to that new selection.
   */
  const [start, end] = Range.edges(
    AlineTransforms.nudgeRangeToEdges(editor, {
      at: editor.selection,
      match: Queries.linkNudgeInlineMatch(editor),
    })
  );
  // Remove cardinality of the start/end to avoid runtime crashes...
  Promise.resolve({ anchor: start, focus: end }).then((insertLinkRange: Range) => {
    insertLinkPopoverVar({
      insertLinkRange,
      selectedText: Queries.string(editor, { at: insertLinkRange, voids: true }),
    });
  });
}

export function InsertButtons(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const rWScrollOffset = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);
  const isInsertLinkDisabled = useSlateSelector(editor => {
    if (!editor.selection) return true;
    return Queries.rangeIncludesCheckboxElement(editor, editor.selection);
  });

  function handleInsertLink(): void {
    attachInsertLinkPopper(editor);
  }

  function handleSelectHeader(): void {
    const headersEntry = Queries.getHeadersElement(editor);
    if (!headersEntry) return;

    const pageHeightPx = Queries.activePageHeight(editor, 'px');
    const activeHeaderTypes = Queries.activeHeaderTypes(editor);
    const totalPageHeightPx = pageHeightPx + pageSeparationPx;
    const firstPageIndexInView = Math.floor(rWScrollOffset / totalPageHeightPx);
    const firstPageNumInView = firstPageIndexInView + 1;
    const isEvenPage = isEvenNum(firstPageNumInView);

    if (firstPageNumInView === 1 && activeHeaderTypes.includes(HeaderType.FirstPage)) {
      const [, firstPageHeaderPath] = Queries.getHeaderOrFooter({
        editor,
        partEntry: headersEntry,
        type: HeaderType.FirstPage,
      });
      const startFirstPageHeader = Editor.start(editor, firstPageHeaderPath);

      AlineTransforms.select(editor, startFirstPageHeader);
      ClientTransforms.focus(editor);

      return;
    }

    if (isEvenPage && activeHeaderTypes.includes(HeaderType.Even)) {
      const [, evenHeaderPath] = Queries.getHeaderOrFooter({
        editor,
        partEntry: headersEntry,
        type: HeaderType.Even,
      });
      const startEvenHeader = Editor.start(editor, evenHeaderPath);

      AlineTransforms.select(editor, startEvenHeader);
      ClientTransforms.focus(editor);

      return;
    }

    const [, defaultHeaderPath] = Queries.getHeaderOrFooter({
      editor,
      partEntry: headersEntry,
      type: HeaderType.Default,
    });
    const startDefaultHeader = Editor.start(editor, defaultHeaderPath);

    AlineTransforms.select(editor, startDefaultHeader);
    ClientTransforms.focus(editor);
  }

  function handleSelectFooter(): void {
    const footersEntry = Queries.getFootersElement(editor);

    if (!footersEntry) return;

    const pageHeightPx = Queries.activePageHeight(editor, 'px');
    const activeFooterTypes = Queries.activeFooterTypes(editor);
    const totalPageHeightPx = pageHeightPx + pageSeparationPx;
    const firstPageIndexInView = Math.floor(rWScrollOffset / totalPageHeightPx);
    const firstPageNumInView = firstPageIndexInView + 1;
    const isEvenPage = isEvenNum(firstPageNumInView);

    if (firstPageNumInView === 1 && activeFooterTypes.includes(FooterType.FirstPage)) {
      const [, firstPageFooterPath] = Queries.getHeaderOrFooter({
        editor,
        partEntry: footersEntry,
        type: FooterType.FirstPage,
      });
      const startFirstPageFooter = Editor.start(editor, firstPageFooterPath);

      AlineTransforms.select(editor, startFirstPageFooter);
      ClientTransforms.focus(editor);

      return;
    }

    if (isEvenPage && activeFooterTypes.includes(FooterType.Even)) {
      const [, evenFooterPath] = Queries.getHeaderOrFooter({
        editor,
        partEntry: footersEntry,
        type: FooterType.Even,
      });
      const startEvenFooter = Editor.start(editor, evenFooterPath);

      AlineTransforms.select(editor, startEvenFooter);
      ClientTransforms.focus(editor);

      return;
    }

    const [, defaultFooterPath] = Queries.getHeaderOrFooter({
      editor,
      partEntry: footersEntry,
      type: FooterType.Default,
    });
    const startDefaultFooter = Editor.start(editor, defaultFooterPath);

    AlineTransforms.select(editor, startDefaultFooter);
    ClientTransforms.focus(editor);
  }

  return (
    <ButtonsBarWithOverflowMenu>
      <PageBreakButton />
      <SelectButton
        data-cy="insert-link-button"
        disabled={editorLoading || isInsertLinkDisabled}
        onClick={handleInsertLink}
      >
        <LinkIcon />
        <Typography className={classes.buttonText} color="inherit" variant="caption">
          Link
        </Typography>
      </SelectButton>
      <SelectButton disabled={editorLoading} onClick={handleSelectHeader}>
        <HeaderIcon />
        <Typography className={classes.buttonText} color="inherit" variant="caption">
          Header
        </Typography>
      </SelectButton>
      <SelectButton disabled={editorLoading} onClick={handleSelectFooter}>
        <FooterIcon />
        <Typography className={classes.buttonText} color="inherit" variant="caption">
          Footer
        </Typography>
      </SelectButton>
      <InsertTableButton />
      <InsertImageButton />
      <PageNumbersButton />
      <TableOfContentsButton />
      <CrossReferenceButton />
    </ButtonsBarWithOverflowMenu>
  );
}
